import { useMutation } from '@apollo/client';
import { graphql } from '@gql/gql';
import type {
  DeleteMessageMutationMutation,
  DeleteMessageMutationMutationVariables,
} from '@gql/graphql';
import { useCallback } from 'react';
import { ACCOUNT_GET_MESSAGES } from './useGetMessages';

export const DELETE_MESSAGE = graphql(`
  mutation DeleteMessageMutation($messageId: Int!) {
    deleteMessage(id: $messageId) {
      id
    }
  }
`);

export const useDeleteMessage = (otherAccountId = 0) => {
  const [
    deleteMessageMutation,
    { error: mutationError, loading: mutationLoading, data: mutationData },
  ] = useMutation<DeleteMessageMutationMutation, DeleteMessageMutationMutationVariables>(
    DELETE_MESSAGE,
    {
      awaitRefetchQueries: true,
      refetchQueries: [ACCOUNT_GET_MESSAGES],
      update(cache, { data }) {
        const cachedData = cache.readQuery({
          query: ACCOUNT_GET_MESSAGES,
          variables: { otherAccountId },
        });
        const messages = (cachedData?.messages ?? []).filter(
          (msg) => msg.id !== data?.deleteMessage.id,
        );
        cache.writeQuery({
          query: ACCOUNT_GET_MESSAGES,
          data: {
            ...cachedData,
            messages,
          },
          variables: {
            otherAccountId,
          },
        });
      },
    },
  );

  const deleteMessage = useCallback(
    async (messageId: number) => {
      await deleteMessageMutation({
        variables: { messageId },
        optimisticResponse: {
          __typename: 'Mutation',
          deleteMessage: {
            id: messageId,
          },
        },
      });
    },
    [deleteMessageMutation],
  );

  return {
    deleteMessage,
    deleteMessageLoading: mutationLoading,
    deleteMessageError: mutationError,
    deleteMessageData: mutationData,
  };
};
